import { flags } from '../core/flags';

/**
 * Terrain recipe for a hollow. Height h is in metres *toward the hollow's
 * centre* (higher = closer to the sun). Sea level is h = 0.
 *
 * Every noise layer is a list of octaves with an explicit frequency, so the
 * CPU can compute exact per-tile, per-octave lattice offsets in f64 (see
 * TileGenerator). That's what keeps kilometre-scale hollows free of f32 noise
 * artefacts: the GPU only ever adds small tile-local offsets.
 */
export interface NoiseLayer {
  /** Wavelength of the first octave, metres. */
  wavelength: number;
  octaves: number;
  lacunarity: number;
  gain: number;
}

/**
 * Erosion: gullies and the ridges between them, cut into the slopes and running down them
 * (stripes along the slope, see terrainNoise's stripesd), each octave narrower and following
 * the slopes of the one before, so small gullies run into bigger ones. How deep they cut scales
 * with the slope they sit on: flat ground keeps none.
 */
export interface ErosionLayer {
  /** Spacing of the widest gullies (m). */
  wavelength: number;
  octaves: number;
  /** Each octave's gullies are this many times narrower. */
  lacunarity: number;
  /** Each octave's depth, against the one before (on top of its narrower spacing). */
  gain: number;
  /** How steep the gullies' sides get, as a share of the slope they cut into. */
  strength: number;
}

/**
 * How the local relief (the ridges', hills' and detail's octaves) is tapered below a wavelength:
 * an octave of wavelength λ shorter than `wavelength` keeps (λ / wavelength)^power of its
 * height. Without it every octave adds about as much slope as the last (more, on the ridges),
 * and the ground is as jagged at a few metres as the mountains are at a few kilometres.
 */
export interface Smoothing {
  /** Wavelength (m) below which relief is tapered. */
  wavelength: number;
  power: number;
}

export interface TerrainProfile {
  /** How finer relief is tapered (see Smoothing; SMOOTHING if left out). */
  smoothing?: Smoothing;
  /** Large-scale land/sea shape. */
  continents: NoiseLayer;
  continentHeight: number;
  /** Shifts the land/sea balance; higher = more land. */
  landBias: number;
  /** Where mountain ranges are allowed. */
  mountainMask: NoiseLayer;
  /** Derivative-damped ridged noise (erosion-like valleys, crisp crests). */
  ridges: NoiseLayer;
  ridgeHeight: number;
  ridgeDamping: number;
  hills: NoiseLayer;
  hillHeight: number;
  detail: NoiseLayer;
  detailHeight: number;
  /** Gullies cut into the slopes (none if left out). */
  erosion?: ErosionLayer;
  /** Height above which snow can settle (as palettes see heights: see `heightScale`). */
  snowLine: number;
  /**
   * Palettes and vegetation see heights divided by this (default 1). A world shrunk by
   * scaleProfile keeps its big world's bands this way (beaches, shore glow, strata, snow
   * and tree lines); the ground itself keeps the real heights.
   */
  heightScale?: number;
}

export const VERDANT_TERRAIN: TerrainProfile = {
  continents: { wavelength: 9000, octaves: 5, lacunarity: 2.03, gain: 0.5 },
  continentHeight: 260,
  landBias: 0.18,
  mountainMask: { wavelength: 7000, octaves: 2, lacunarity: 2.1, gain: 0.5 },
  ridges: { wavelength: 3200, octaves: 8, lacunarity: 2.01, gain: 0.52 },
  ridgeHeight: 1250,
  ridgeDamping: 1.6,
  hills: { wavelength: 700, octaves: 4, lacunarity: 2.05, gain: 0.5 },
  hillHeight: 55,
  detail: { wavelength: 72, octaves: 6, lacunarity: 2.07, gain: 0.48 },
  detailHeight: 3,
  erosion: { wavelength: 520, octaves: 5, lacunarity: 1.97, gain: 0.55, strength: 0.4 },
  snowLine: 780,
};

/**
 * A recipe for a smaller world: land shapes scale by `k` and heights by `relief`, but the
 * fine detail stays at walking scale. With relief = k slopes stay as walkable as before.
 * Palettes still see the big world's heights, so its beaches and snow lines shrink with it.
 */
export function scaleProfile(p: TerrainProfile, k: number, relief: number): TerrainProfile {
  const scaled = (l: NoiseLayer, min = 0): NoiseLayer => ({
    ...l,
    wavelength: Math.max(min, l.wavelength * k),
  });
  return {
    ...p,
    continents: scaled(p.continents),
    continentHeight: p.continentHeight * relief,
    mountainMask: scaled(p.mountainMask),
    ridges: scaled(p.ridges),
    ridgeHeight: p.ridgeHeight * relief,
    hills: scaled(p.hills, 60),
    hillHeight: p.hillHeight * relief,
    erosion: p.erosion && { ...p.erosion, wavelength: Math.max(40, p.erosion.wavelength * k) },
    heightScale: (p.heightScale ?? 1) * relief,
  };
}

type N = any;

/** The height palettes and vegetation see for a ground height node `h` (see `heightScale`). */
export function paletteHeight(p: TerrainProfile, h: N): N {
  const s = p.heightScale ?? 1;
  return s === 1 ? h : h.div(s);
}

/**
 * Every world's, unless its profile says otherwise. It cut the slope a walker meets over 2 m by
 * about two thirds on mountainsides and by half on flat ground, leaving the landforms (the
 * slope over 128 m) as they were. `?smooth=0` leaves the relief untapered, for comparisons.
 */
export const SMOOTHING: Smoothing = flags.bool('smooth', true)
  ? { wavelength: 300, power: 1 }
  : { wavelength: 0, power: 1 };

/** The share of its height an octave of local relief keeps at wavelength `lambda` (see Smoothing). */
export function octaveTaper(p: TerrainProfile, lambda: number): number {
  const s = p.smoothing ?? SMOOTHING;
  return lambda >= s.wavelength ? 1 : (lambda / s.wavelength) ** s.power;
}

/** Flattened octave table in the order the GPU height function consumes it. */
export function octaveFrequencies(p: TerrainProfile): number[] {
  const out: number[] = [];
  const layers: { wavelength: number; octaves: number; lacunarity: number }[] = [
    p.continents,
    p.mountainMask,
    p.ridges,
    p.hills,
    p.detail,
  ];
  if (p.erosion) layers.push(p.erosion);
  for (const layer of layers) {
    let f = 1 / layer.wavelength;
    for (let i = 0; i < layer.octaves; i++) {
      out.push(f);
      f *= layer.lacunarity;
    }
  }
  return out;
}

/** Erosion never cuts into more slope than this (gradient length; 1 is 45°). */
export const EROSION_SLOPE_CAP = 0.75;
/**
 * Gullies are V-shaped and the crests between them rounded, as water cuts them: each stripe
 * value v becomes v − ROUNDING·v² (steeper toward the gully's bottom, flatter at the crest).
 */
export const EROSION_ROUNDING = 0.35;

/** The deepest the gullies can cut (m): every octave at its full depth, on the steepest slope. */
export function erosionBound(p: TerrainProfile): number {
  const e = p.erosion;
  if (!e) return 0;
  let sum = 0;
  let lambda = e.wavelength;
  let amp = 1;
  for (let i = 0; i < e.octaves; i++) {
    sum += (e.strength * EROSION_SLOPE_CAP * (1 + EROSION_ROUNDING) * lambda * amp) / (2 * Math.PI);
    lambda /= e.lacunarity;
    amp *= e.gain;
  }
  return sum;
}

/** Rough bound on |h|, for culling before a tile's real range is known. */
export function heightBound(p: TerrainProfile): number {
  return (
    p.continentHeight * (1 + p.landBias) +
    p.ridgeHeight * 1.2 +
    p.hillHeight +
    p.detailHeight +
    erosionBound(p)
  );
}
